import React, { useEffect, useState } from 'react';
import { Tags, RefreshCw } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { backofficeDb } from '../backofficeFirebaseClient';

const CouponsManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const snap = await getDocs(collection(backofficeDb, 'coupons'));
      setCoupons(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLastSyncedAt(new Date().toISOString());
    } catch (e) {
      setError(e.message || 'Could not read coupons (check Firestore rules / collection).');
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Tags className="w-5 h-5 text-purple-600" />
          <h2 className="text-xl font-semibold">Coupons</h2>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      {lastSyncedAt && <p className="text-xs text-gray-500">Last synced: {lastSyncedAt}</p>}
      {error && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">{error}</div>}
      <p className="text-sm text-gray-600">
        Read-only view of <code>coupons</code> in the backoffice project. Create/edit documents in Firestore or add
        dedicated APIs when rules are ready.
      </p>
      <div className="bg-white border rounded-lg overflow-auto max-h-[60vh]">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-2 border-b">ID</th>
              <th className="p-2 border-b">Code</th>
              <th className="p-2 border-b">Summary</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id} className="border-b last:border-0">
                <td className="p-2 font-mono text-xs">{c.id}</td>
                <td className="p-2">{c.code || c.couponCode || '—'}</td>
                <td className="p-2 text-xs text-gray-700">{c.description || JSON.stringify(c).slice(0, 120)}</td>
              </tr>
            ))}
            {!loading && coupons.length === 0 && !error && (
              <tr>
                <td colSpan={3} className="p-4 text-gray-500">
                  No coupons found. Add documents under <code>coupons</code> to see them here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CouponsManager;
